import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../../components/ui/Card';
import { Button } from '../../../components/ui/Button';
import { Badge } from '../../../components/ui/Badge';
import { Plus, Monitor, Edit, Trash2 } from 'lucide-react';
import DataTable from '../../../components/ui/DataTable';
import toast from 'react-hot-toast';
import { masterMenuModuleService } from '../../../api/masterMenuModuleService';
import { extractErrorMessage } from '../../../utils/errorHandler';
import CreateScreenModal from '../components/CreateScreenModal';
import ConfirmationModal from '../../../components/modals/ConfirmationModal';

/**
 * Screen Creation Screen
 * 
 * Displays a table of existing screens with options to create, edit, and delete.
 * 
 * @component
 */
const ScreenCreation = () => {
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingScreen, setEditingScreen] = useState(null);
  const [screens, setScreens] = useState([]);
  const [loading, setLoading] = useState(false);
  const [creating, setCreating] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [screenToDelete, setScreenToDelete] = useState(null);

  // Fetch screens on component mount
  useEffect(() => {
    loadScreens();
  }, []);

  const loadScreens = async () => {
    try {
      setLoading(true);
      // Use getScreensGroupedByModuleIncludingInactive to get both active and inactive screens
      const response = await masterMenuModuleService.getScreensGroupedByModuleIncludingInactive();
      const screensData = response?.data || {};
      
      // Flatten the grouped data into a single array (keep full screen object for editing)
      const allScreens = [];
      Object.keys(screensData).forEach(moduleName => {
        const moduleScreens = screensData[moduleName] || [];
        moduleScreens.forEach(screen => {
          allScreens.push({
            id: screen.id,
            screenName: screen.screenName || screen.screen_name,
            screenLable: screen.screenLable || screen.screen_label || 'N/A',
            parentModule: moduleName,
            masterMenuModuleId: screen.masterMenuModuleId || screen.module_id,
            routePath: screen.routing || screen.routePath || 'N/A',
            status: screen.isActive ? 'Active' : 'Inactive',
            canView: screen.canView !== undefined ? screen.canView : false,
            canInsert: screen.canInsert !== undefined ? screen.canInsert : false,
            canUpdate: screen.canUpdate !== undefined ? screen.canUpdate : false,
            canDelete: screen.canDelete !== undefined ? screen.canDelete : false,
            orderNo: screen.orderNo || screen.order_no || null,
            fafaIcon: screen.fafaIcon || screen.fafa_icon || '',
            isDashboard: screen.isDashboard !== undefined ? screen.isDashboard : false,
            createdonTime: screen.createdonTime || screen.created_on || screen.createdOnTime,
            // Keep full screen object for editing
            _screenData: screen
          });
        });
      });

      // Sort by ID ascending so IDs appear sequential by default
      allScreens.sort((a, b) => {
        const idA = Number(a.id) || 0;
        const idB = Number(b.id) || 0;
        return idA - idB;
      });

      setScreens(allScreens);
    } catch (error) {
      console.error('Error loading screens:', error);
      toast.error(extractErrorMessage(error, 'Failed to load screens'));
      setScreens([]);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateScreen = async (screenData) => {
    try {
      setCreating(true);
      let response;
      
      if (editingScreen) {
        // Update existing screen
        response = await masterMenuModuleService.updateScreen(editingScreen.id, screenData);
        if (response?.data) {
          toast.success('Screen updated successfully!');
        } else {
          throw new Error('Failed to update screen');
        }
      } else {
        // Create new screen
        response = await masterMenuModuleService.createScreen(screenData);
        if (response?.data) {
          toast.success('Screen created successfully!');
        } else {
          throw new Error('Failed to create screen');
        }
      }
      
      setShowCreateModal(false);
      setEditingScreen(null);
      await loadScreens(); // Refresh the table
    } catch (error) {
      console.error(`Error ${editingScreen ? 'updating' : 'creating'} screen:`, error);
      toast.error(extractErrorMessage(error, `Failed to ${editingScreen ? 'update' : 'create'} screen`));
    } finally {
      setCreating(false);
    }
  };

  const handleEditScreen = (screen) => {
    setEditingScreen(screen._screenData || screen);
    setShowCreateModal(true);
  };

  const handleDeleteClick = (screen) => {
    setScreenToDelete(screen);
    setShowDeleteConfirm(true);
  };

  const handleDeleteConfirm = async () => {
    if (!screenToDelete) return;
    
    try {
      setDeleting(true);
      const response = await masterMenuModuleService.deleteScreen(screenToDelete.id);
      
      if (response?.data || response) {
        toast.success('Screen deleted successfully (set to inactive)');
        setShowDeleteConfirm(false);
        setScreenToDelete(null);
        await loadScreens(); // Refresh the table
      } else {
        throw new Error('Failed to delete screen');
      }
    } catch (error) {
      console.error('Error deleting screen:', error);
      toast.error(extractErrorMessage(error, 'Failed to delete screen'));
    } finally {
      setDeleting(false);
    }
  };

  const getStatusBadgeColor = (status) => {
    switch (status?.toLowerCase()) {
      case 'active':
        return 'bg-green-100 text-green-800 border-green-200';
      case 'inactive':
        return 'bg-gray-100 text-gray-800 border-gray-200';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const getPermissionBadge = (value) => {
    return value ? (
      <Badge variant="outline" className="bg-green-100 text-green-800 border-green-200 text-xs">
        Yes
      </Badge>
    ) : (
      <Badge variant="outline" className="bg-gray-100 text-gray-800 border-gray-200 text-xs">
        No
      </Badge>
    );
  };

  const columns = [
    {
      key: 'id',
      label: 'ID',
      render: (value) => (
        <span className="text-sm font-medium text-gray-900 dark:text-gray-100">
          {value}
        </span>
      )
    },
    {
      key: 'screenName',
      label: 'Screen Name',
      render: (value) => (
        <div className="font-semibold text-gray-900 dark:text-gray-100">
          {value || 'N/A'}
        </div>
      )
    },
    // {
    //   key: 'screenLable',
    //   label: 'Screen Label',
    //   render: (value) => (
    //     <div className="text-sm text-gray-600 dark:text-gray-400">
    //       {value || 'N/A'}
    //     </div>
    //   )
    // },
    {
      key: 'parentModule',
      label: 'Parent Module',
      render: (value) => (
        <div className="text-sm text-gray-600 dark:text-gray-400">
          {value || 'N/A'}
        </div>
      )
    },
    {
      key: 'routePath',
      label: 'Route Path',
      render: (value) => (
        <div className="text-sm text-gray-600 dark:text-gray-400 font-mono">
          {value || 'N/A'}
        </div>
      )
    },
    // {
    //   key: 'orderNo',
    //   label: 'Order No',
    //   render: (value) => (
    //     <span className="text-sm text-gray-600 dark:text-gray-400">
    //       {value !== null && value !== undefined ? value : 'N/A'}
    //     </span>
    //   )
    // },
    // {
    //   key: 'fafaIcon',
    //   label: 'Icon',
    //   render: (value) => (
    //     <div className="text-sm text-gray-600 dark:text-gray-400">
    //       {value ? (
    //         <span className="font-mono">{value}</span>
    //       ) : 'N/A'}
    //     </div>
    //   )
    // },
    {
      key: 'canView',
      label: 'Can View',
      render: (value) => getPermissionBadge(value)
    },
    // {
    //   key: 'canInsert',
    //   label: 'Can Insert',
    //   render: (value) => getPermissionBadge(value)
    // },
    // {
    //   key: 'canUpdate',
    //   label: 'Can Update',
    //   render: (value) => getPermissionBadge(value)
    // },
    // {
    //   key: 'canDelete',
    //   label: 'Can Delete',
    //   render: (value) => getPermissionBadge(value)
    // },
    // {
    //   key: 'isDashboard',
    //   label: 'Dashboard',
    //   render: (value) => (
    //     <Badge variant="outline" className={value ? 'bg-blue-100 text-blue-800 border-blue-200' : 'bg-gray-100 text-gray-800 border-gray-200'}>
    //       {value ? 'Yes' : 'No'}
    //     </Badge>
    //   )
    // },
    {
      key: 'status',
      label: 'Status',
      render: (value) => (
        <Badge variant="outline" className={getStatusBadgeColor(value)}>
          {value || 'N/A'}
        </Badge>
      )
    },
    {
      key: 'createdonTime',
      label: 'Created Date',
      render: (value) => (
        <span className="text-sm text-gray-600 dark:text-gray-400">
          {value ? new Date(value).toLocaleDateString() : 'N/A'}
        </span>
      )
    },
    {
      key: 'actions',
      label: 'Actions',
      render: (value, row) => (
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            title="Edit Screen"
            onClick={() => handleEditScreen(row)}
            className="text-blue-600 hover:text-blue-700"
          >
            <Edit className="h-4 w-4" />
          </Button>
          <Button
            variant="outline"
            size="sm"
            title="Delete Screen"
            onClick={() => handleDeleteClick(row)}
            className="text-red-600 hover:text-red-700"
          >
            <Trash2 className="h-4 w-4" />
          </Button>
        </div>
      )
    }
  ];

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative py-8 sm:py-12 px-4 sm:px-6 overflow-hidden">
        {/* Background */}
        <div
          className="absolute inset-0"
          style={{
            backgroundImage: 'url(/background-image.jpg)',
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            backgroundRepeat: 'no-repeat',
            filter: 'blur(2px)',
            opacity: 0.3
          }}
        ></div>
        <div className="absolute inset-0 bg-white/70 dark:bg-gray-900/70"></div>

        <div className="container mx-auto relative z-10">
          <div className="flex flex-col lg:flex-row lg:justify-between lg:items-center gap-4">
            <div className="space-y-2 sm:space-y-3">
              <div className="inline-block px-3 sm:px-4 py-1.5 sm:py-2 bg-primary/10 border border-primary/20 rounded-full">
                <span className="text-xs sm:text-sm font-semibold text-primary">Administration</span>
              </div>
              <h1 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-foreground drop-shadow-lg">
                Screen Creation
              </h1>
              <p className="text-sm sm:text-base lg:text-lg text-muted-foreground">
                Create and manage application screens
              </p>
            </div>
            <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2 sm:gap-3 w-full sm:w-auto">
              <Button
                className="gradient-primary text-primary-foreground shadow-elegant hover:shadow-glow w-full sm:w-auto"
                onClick={() => setShowCreateModal(true)}
              >
                <Plus className="h-4 w-4 mr-2" />
                Create Screen
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Content */}
      <div className="p-4 sm:p-6 relative">
        {/* Screens Table */}
        <Card className="group hover:shadow-luxury transition-smooth border-2 hover:border-primary/30 overflow-hidden relative gradient-card">
          <div className="absolute inset-0 bg-gradient-to-br from-primary/5 to-accent/5 opacity-0 group-hover:opacity-100 transition-opacity"></div>
          <CardHeader className="relative z-10">
            <div className="flex items-center gap-3 justify-between">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-gradient-to-br from-primary to-accent shadow-glow">
                  <Monitor className="h-5 w-5 text-primary-foreground" />
                </div>
                <div>
                  <CardTitle>All Screens</CardTitle>
                  <CardDescription>{screens.length} screens registered</CardDescription>
                </div>
              </div>
            </div>
          </CardHeader>
          <CardContent className="relative z-10">
            {loading ? (
              <div className="flex flex-col items-center justify-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
                <p className="text-muted-foreground mt-3">Loading screens...</p>
              </div>
            ) : screens.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-8">
                <Monitor className="h-12 w-12 text-gray-400 mb-4" />
                <p className="text-muted-foreground">No screens found</p>
              </div>
            ) : (
              <DataTable
                data={screens}
                columns={columns}
                pagination={false}
                searchable={false}
                sortable={true}
                itemsPerPage={10}
                showItemsPerPageSelector={false}
              />
            )}
          </CardContent>
        </Card>
      </div>

      {/* Create/Edit Screen Modal */}
      <CreateScreenModal
        isOpen={showCreateModal}
        onClose={() => {
          setShowCreateModal(false);
          setEditingScreen(null);
        }}
        onSubmit={handleCreateScreen}
        loading={creating}
        editingScreen={editingScreen}
      />

      {/* Delete Confirmation Modal */}
      <ConfirmationModal
        isOpen={showDeleteConfirm}
        onClose={() => {
          setShowDeleteConfirm(false);
          setScreenToDelete(null);
        }}
        onConfirm={handleDeleteConfirm}
        title="Delete Screen"
        message={`Are you sure you want to delete the screen "${screenToDelete?.screenName}"? This will set it to inactive.`}
        confirmText={deleting ? "Deleting..." : "Delete"}
        cancelText="Cancel"
        type="danger"
      />
    </div>
  );
};

export default ScreenCreation;

